import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { UserCircle, IdentificationBadge, FileText, Certificate, Camera, Trash, FloppyDisk, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const emptyForm = {
  specializations: '',
  bio: '',
  hourly_rate: '',
  location: '',
  vat_number: '',
  business_name: '',
  is_public_photo_visible: true,
  region: '',
  province: '',
  city: '',
  comune: '',
};

const ProfessionalProfileEdit = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [profile, setProfile] = useState(null);
  const [files, setFiles] = useState({ cv: [], certification: [], public_photo: [], generic: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const cvInputRef = useRef(null);
  const certInputRef = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'professional') {
      toast.error('Area riservata ai professionisti');
      navigate('/');
      return;
    }
    if (user.professional_status !== 'approved') {
      // still show empty screen with banner
      setLoading(false);
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    api.get('/api/geo/italian-regions').then((r) => setRegions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.region || !regions.length) { setProvinces([]); return; }
    const row = regions.find((r) => r.region === form.region);
    setProvinces(row?.provinces || []);
  }, [form.region, regions]);

  const fetchData = async () => {
    try {
      const [profRes, filesRes] = await Promise.all([
        api.get('/api/professionals/me').catch(() => ({ data: null })),
        api.get('/api/files/mine').catch(() => ({ data: [] })),
      ]);
      if (profRes.data) {
        setProfile(profRes.data);
        setForm({
          specializations: (profRes.data.specializations || []).join(', '),
          bio: profRes.data.bio || '',
          hourly_rate: profRes.data.hourly_rate?.toString() || '',
          location: profRes.data.location || '',
          vat_number: profRes.data.vat_number || '',
          business_name: profRes.data.business_name || '',
          is_public_photo_visible: profRes.data.is_public_photo_visible !== false,
          region: profRes.data.region || '',
          province: profRes.data.province || '',
          city: profRes.data.city || '',
          comune: profRes.data.comune || '',
        });
      }
      const byType = { cv: [], certification: [], public_photo: [], generic: [] };
      (filesRes.data || []).forEach(f => {
        const t = f.doc_type || 'generic';
        if (byType[t]) byType[t].push(f);
      });
      setFiles(byType);
    } catch {
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        specializations: form.specializations.split(',').map(s => s.trim()).filter(Boolean),
        bio: form.bio,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        location: form.location,
        vat_number: form.vat_number.trim() || null,
        business_name: form.business_name.trim() || null,
        is_public_photo_visible: form.is_public_photo_visible,
        region: form.region || null,
        province: form.province || null,
        city: form.city || null,
        comune: form.comune || null,
      };
      if (!profile) {
        await api.post('/api/professionals/profile', payload);
        toast.success('Profilo creato');
      } else {
        await api.put('/api/professionals/profile', payload);
        toast.success('Profilo aggiornato');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const handleUpload = async (fileObj, docType) => {
    if (!fileObj) return;
    if (fileObj.size > 10 * 1024 * 1024) {
      toast.error('File max 10MB');
      return;
    }
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append('file', fileObj);
      fd.append('doc_type', docType);
      const res = await api.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File caricato');
      // Link to professional profile
      const patch = {};
      if (docType === 'cv') patch.cv_file_id = res.data.file_id;
      if (docType === 'public_photo') patch.public_photo_file_id = res.data.file_id;
      if (docType === 'certification') patch.certification_file_ids = [...(profile?.certification_file_ids || []), res.data.file_id];
      if (Object.keys(patch).length && profile) {
        await api.put('/api/professionals/profile', patch);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Eliminare il file?')) return;
    try {
      await api.delete(`/api/files/${fileId}`);
      toast.success('File eliminato');
      fetchData();
    } catch { toast.error('Errore eliminazione'); }
  };

  const downloadPrivateFile = async (fileId, filename) => {
    try {
      const res = await api.get(`/api/files/${fileId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast.error('Errore download'); }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const publicPhotoFile = files.public_photo.find(f => f.file_id === profile?.public_photo_file_id);
  const publicPhotoUrl = publicPhotoFile ? `${backendUrl}/api/files/public/${publicPhotoFile.file_id}` : null;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (user?.professional_status !== 'approved') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 py-20">
          <Card className="neumorphism rounded-2xl p-8 text-center" data-testid="not-approved-banner">
            <h1 className="font-heading font-semibold text-2xl mb-3">Account non ancora approvato</h1>
            <p className="text-foreground/70">
              Potrai gestire il tuo profilo e caricare documenti dopo l&apos;approvazione da parte del team VivaLink.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-semibold text-4xl text-foreground mb-2" data-testid="profile-edit-title">
            Il Mio Profilo
          </h1>
          <p className="text-foreground/70">Gestisci dati professionali, documenti e foto pubblica</p>
        </div>

        {/* Banner Prestazioni & Quote */}
        <Card className="rounded-2xl p-4 mb-6 border-l-4 border-l-amber-500 bg-amber-50" data-testid="quote-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">⚠️ Consigliamo di modificare le quote delle tue prestazioni</p>
              <p>Le prestazioni sono caricate con prezzi medi nazionali di default. Se non modifichi le quote, il sistema pubblicherà i tuoi prezzi con i valori di default e accetterai le quote pubblicate. Personalizzale in base alla tua tariffa reale.</p>
            </div>
            <Button size="sm" className="rounded-full" onClick={() => navigate('/professional/prestazioni')} data-testid="go-prestazioni-btn">
              Gestisci Prestazioni →
            </Button>
          </div>
        </Card>

        {/* Dati anagrafici / profilo */}
        <Card className="neumorphism rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <UserCircle size={22} className="text-primary" weight="duotone" />
            <h2 className="font-heading font-medium text-xl">Dati Professionali</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Specializzazioni (separate da virgola)</Label>
              <Input value={form.specializations} onChange={e => setForm({ ...form, specializations: e.target.value })} placeholder="Fisioterapia, Riabilitazione" data-testid="specializations-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Bio</Label>
              <Textarea rows={4} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="Racconta la tua esperienza..." data-testid="bio-input" />
            </div>
            <div>
              <Label>Tariffa oraria (€)</Label>
              <Input type="number" min="0" step="0.5" value={form.hourly_rate} onChange={e => setForm({ ...form, hourly_rate: e.target.value })} data-testid="hourly-rate-input" />
            </div>
            <div>
              <Label>Zona operativa (libera)</Label>
              <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Es. Milano centro / Zone servite" data-testid="location-input" />
            </div>
            <div>
              <Label>Regione</Label>
              <select
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value, province: '' })}
                className="w-full h-10 border border-border rounded-md px-3 bg-background"
                data-testid="select-region"
              >
                <option value="">Seleziona…</option>
                {regions.map((r) => <option key={r.region} value={r.region}>{r.region}</option>)}
              </select>
            </div>
            <div>
              <Label>Provincia</Label>
              <select
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                disabled={!provinces.length}
                className="w-full h-10 border border-border rounded-md px-3 bg-background disabled:opacity-50"
                data-testid="select-province"
              >
                <option value="">Seleziona…</option>
                {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label>Città</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Es. Milano" data-testid="input-city" />
            </div>
            <div>
              <Label>Comune (opzionale)</Label>
              <Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} placeholder="Frazione o zona specifica" data-testid="input-comune" />
            </div>
          </div>
        </Card>

        {/* Dati fiscali - Fase 2A */}
        <Card className="neumorphism rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <IdentificationBadge size={22} className="text-accent" weight="duotone" />
            <h2 className="font-heading font-medium text-xl">Dati Fiscali</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Partita IVA</Label>
              <Input value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} placeholder="IT12345678901" data-testid="vat-input" />
            </div>
            <div>
              <Label>Ragione sociale</Label>
              <Input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} placeholder="Es. Studio Dott. Rossi SRL" data-testid="business-name-input" />
            </div>
          </div>
        </Card>

        {/* Foto pubblica */}
        <Card className="neumorphism rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Camera size={22} className="text-accent" weight="duotone" />
              <h2 className="font-heading font-medium text-xl">Foto Pubblica</h2>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_public_photo_visible} onCheckedChange={v => setForm({ ...form, is_public_photo_visible: v })} data-testid="photo-visibility-switch" />
              <span className="text-sm text-foreground/70">
                {form.is_public_photo_visible ? 'Visibile nel profilo' : 'Nascosta'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted flex items-center justify-center flex-shrink-0" data-testid="photo-preview">
              {publicPhotoUrl ? (
                <img src={publicPhotoUrl} alt="foto profilo" className="w-full h-full object-cover" />
              ) : (
                <UserCircle size={64} className="text-foreground/30" weight="duotone" />
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={e => handleUpload(e.target.files?.[0], 'public_photo')} data-testid="photo-input" />
              <Button variant="outline" onClick={() => photoInputRef.current?.click()} disabled={uploading === 'public_photo'} data-testid="upload-photo-btn">
                <UploadSimple size={16} className="mr-1" />
                {uploading === 'public_photo' ? 'Caricamento...' : 'Carica Foto'}
              </Button>
              {publicPhotoFile && (
                <Button variant="ghost" onClick={() => handleDeleteFile(publicPhotoFile.file_id)} data-testid="remove-photo-btn">
                  <Trash size={16} className="mr-1 text-destructive" /> Rimuovi
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* CV */}
        <Card className="neumorphism rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <FileText size={22} className="text-primary" weight="duotone" />
              <h2 className="font-heading font-medium text-xl">Curriculum Vitae</h2>
            </div>
            <input ref={cvInputRef} type="file" accept="application/pdf,image/*,.doc,.docx" hidden onChange={e => handleUpload(e.target.files?.[0], 'cv')} data-testid="cv-input" />
            <Button variant="outline" onClick={() => cvInputRef.current?.click()} disabled={uploading === 'cv'} data-testid="upload-cv-btn">
              <UploadSimple size={16} className="mr-1" />
              {uploading === 'cv' ? 'Caricamento...' : 'Carica CV'}
            </Button>
          </div>
          {files.cv.length === 0 ? (
            <p className="text-sm text-foreground/60">Nessun CV caricato</p>
          ) : (
            <div className="space-y-2" data-testid="cv-list">
              {files.cv.map(f => (
                <div key={f.file_id} className={`flex items-center justify-between border rounded-lg p-3 flex-wrap gap-2 ${f.file_id === profile?.cv_file_id ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid={`cv-${f.file_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={18} className="text-primary flex-shrink-0" weight="duotone" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{f.original_filename}</p>
                      <p className="text-xs text-foreground/60">{new Date(f.created_at).toLocaleDateString('it-IT')} • {(f.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {f.file_id === profile?.cv_file_id && <Badge className="bg-primary text-white">Attivo</Badge>}
                    <Button size="sm" variant="ghost" onClick={() => downloadPrivateFile(f.file_id, f.original_filename)}>Scarica</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteFile(f.file_id)}>
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Certifications */}
        <Card className="neumorphism rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Certificate size={22} className="text-accent" weight="duotone" />
              <h2 className="font-heading font-medium text-xl">Certificazioni</h2>
            </div>
            <input ref={certInputRef} type="file" accept="application/pdf,image/*" hidden onChange={e => handleUpload(e.target.files?.[0], 'certification')} data-testid="cert-input" />
            <Button variant="outline" onClick={() => certInputRef.current?.click()} disabled={uploading === 'certification'} data-testid="upload-cert-btn">
              <UploadSimple size={16} className="mr-1" />
              {uploading === 'certification' ? 'Caricamento...' : 'Aggiungi Certificazione'}
            </Button>
          </div>
          {files.certification.length === 0 ? (
            <p className="text-sm text-foreground/60">Nessuna certificazione caricata</p>
          ) : (
            <div className="space-y-2" data-testid="certifications-list">
              {files.certification.map(f => (
                <div key={f.file_id} className="flex items-center justify-between border border-border rounded-lg p-3 flex-wrap gap-2" data-testid={`cert-${f.file_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Certificate size={18} className="text-accent flex-shrink-0" weight="duotone" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{f.original_filename}</p>
                      <p className="text-xs text-foreground/60">{new Date(f.created_at).toLocaleDateString('it-IT')} • {(f.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => downloadPrivateFile(f.file_id, f.original_filename)}>Scarica</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteFile(f.file_id)}>
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="flex justify-end sticky bottom-4">
          <Button size="lg" className="rounded-full shadow-lg" onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
            <FloppyDisk size={18} className="mr-2" />
            {saving ? 'Salvataggio...' : profile ? 'Salva Modifiche' : 'Crea Profilo'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalProfileEdit;
